import React, { useEffect, useRef, useState } from "react"
import { Dimensions } from "~/ui/primitives"
import FastImage from "~/ui/FastImage"
import { useTheme } from '~/ui/styled'
import Icon from "~widgets/icon"
import { FlatList } from "~/ui/gesture-handler"
import { Video as ExpoVideo, AVPlaybackStatus, VideoProps } from "~/ui/expo-av"
import { Mask } from "~widgets/mask"
import styled from '~/ui/styled'
import { Photo, PhotosModelProps } from "./photos-mode"
import { OvalBadge } from "~widgets/badge"

const { width } = Dimensions.get("window")
interface PhotosSelectionProps extends PhotosModelProps {
  setVisible: (visible: boolean) => void
  curSelectedIndex: number | undefined
  setCurSelectedIndex: (index: number) => void
  handleSelectMultiple: (photo: Photo) => void
  photos: Photo[]
}

const Container = styled.SafeAreaView`
  height: 100%;
  position: absolute;
  background-color: black;
  align-items: center;
  justify-content: center;
  flex: 1;
  padding-bottom: 24px;
`

const ImageAndVideoWrap = styled.View`
  width: ${width}px;
  flex: 1;
`
const ImageComponent = styled(FastImage)`
  flex: 1;
`

const Image = ({ uri }) => {
  return (
    <ImageAndVideoWrap>
      <ImageComponent src={{ uri }} resizeMode={FastImage.resizeMode.contain} />
    </ImageAndVideoWrap>
  )
}

const VideComponentInner = styled(ExpoVideo)`
  flex: 1;
`

const VideComponent = (props, ref) => {
  return <VideComponentInner ref={ref} {...props} />
}

const ForwardedVideComponent = React.forwardRef(VideComponent)

const Video = ({
  curSelectedIndex,
  index,
  ...props
}: {
  curSelectedIndex: number
  index: number
  props: VideoProps
}) => {
  const ref = useRef<ExpoVideo>()
  const [status, setStatus] = useState<AVPlaybackStatus | undefined>()

  const play = () => {
    if (ref?.current) {
      ref.current.setIsLoopingAsync(false)
      ref.current.setOnPlaybackStatusUpdate((e) => {
        setStatus(e)
      })
      ref.current.playAsync()
    }
  }

  const pause = () => {
    if (ref?.current) {
      ref.current.pauseAsync()
    }
  }

  useEffect(() => {
    if (curSelectedIndex !== index && ref?.current) {
      ref.current.stopAsync()
    }
  }, [curSelectedIndex, index])

  useEffect(() => {
    if (status && status?.durationMillis === status?.positionMillis) {
      ref.current.setPositionAsync(0)
    }
  }, [status])

  return (
    <ImageAndVideoWrap>
      <ForwardedVideComponent ref={ref} {...props} />
      <Mask onClick={status?.shouldPlay ? pause : play}>
        {!status?.shouldPlay && <Play color="#fff" size={80}></Play>}
      </Mask>
    </ImageAndVideoWrap>
  )
}

const LeaveButtonPressable = styled.Pressable<{ backgroundColor?: string; borderColor?: string }>`
  justify-content: center;
  align-items: center;
  width: 40px;
  height: 40px;
  border-radius: 20px;
  background-color: ${({ theme, backgroundColor }) => backgroundColor || theme.colors.primary};
`

const LeaveButton = ({ onClick }) => (
  <LeaveButtonPressable onClick={onClick}>
    <Icon name="close" size={16} color="white" />
  </LeaveButtonPressable>
)

const SendButtonPressable = styled.Pressable`
  border-style: solid;
  border-width: 2px;
  border-color: ${({ theme }) => theme.colors.primary};
  padding: 5px;
  border-radius: 30px;
  background-color: ${({ theme }) => theme.colors.primary};
`

const SendButtonIconWrap = styled.View`
  transform: rotateZ(-45deg);
`

const SendButton = ({ onClick, badgeCount }) => {
  const theme = useTheme()
  return (
    <SendButtonPressable onClick={onClick}>
      <OvalBadge
        value={badgeCount}
        top="-14px"
        right="-14px"
        backgroundColor={theme.colors.success}
      ></OvalBadge>
      <SendButtonIconWrap>
        <Icon name="fs" size={28} color="white" />
      </SendButtonIconWrap>
    </SendButtonPressable>
  )
}

const TopArea = styled.View`
  width: ${width}px;
  padding: 10px 20px;
  flex-direction: row;
  justify-content: flex-end;
`
const BottomArea = styled.View`
  width: ${width}px;
  padding: 10px 20px;
  flex-direction: row;
  justify-content: space-between;
`

const CheckBoxWrap = styled.Pressable`
  z-index: 1;
  padding: 10px 0px 10px 10px;
`

const Checked = styled.View`
  width: 30px;
  height: 30px;
  border-radius: 15px;
  background-color: ${({ theme }) => theme.colors.primary};
  justify-content: center;
  align-items: center;
`

const UnChecked = styled.View`
  width: 30px;
  height: 30px;
  border-radius: 15px;
  background-color: ${({ theme }) => theme.colors.secondaryText};
  justify-content: center;
  align-items: center;
`

const PhotosSelection: React.FC<PhotosSelectionProps> = ({
  setVisible,
  curSelectedIndex,
  setCurSelectedIndex,
  handleSelectMultiple,
  photos,
  selectedPhotos,
  submitSelectedPhotos,
}) => {
  const theme = useTheme()

  const curPhoto = photos[curSelectedIndex]

  const isCurPhotoSelected = selectedPhotos.findIndex((sPhoto) => sPhoto.id === curPhoto?.id) > -1

  const handleOnScroll = (event) => {
    const scrollViewTotalOffset = event.nativeEvent.contentOffset.x
    const index = Math.round(scrollViewTotalOffset / width)
    setCurSelectedIndex(index)
  }

  const selectCurPhoto = () => {
    handleSelectMultiple(curPhoto)
  }

  const send = () => {
    if (selectedPhotos.length === 0) {
      submitSelectedPhotos([curPhoto])
    } else {
      submitSelectedPhotos(selectedPhotos)
    }
  }

  return (
    <Container>
      <TopArea>
        <CheckBoxWrap onClick={selectCurPhoto}>
          {isCurPhotoSelected ? (
            <Checked>
              <Icon name="chenggong" size={24} color={theme.colors.blockBackground} />
            </Checked>
          ) : (
            <UnChecked>
              <Icon name="yuan" size={24} color={theme.colors.blockBackground} />
            </UnChecked>
          )}
        </CheckBoxWrap>
      </TopArea>
      <FlatList
        scrollEventThrottle={400}
        showsVerticalScrollIndicator={false}
        pagingEnabled
        horizontal
        getItemLayout={(data, index) => ({ length: width, offset: width * index, index })}
        keyExtractor={(item: any) => item.id}
        extraData={photos.length}
        data={photos}
        initialScrollIndex={curSelectedIndex}
        renderItem={({ item, index }: any) => {
          if (item.mediaType === "photo") {
            return <Image uri={item.uri} />
          } else if (item.mediaType === "video") {
            return (
              <Video
                curSelectedIndex={curSelectedIndex}
                index={index}
                src={item.uri}
                resizeMode="contain"
                shouldPlay={false}
                isLooping
              />
            )
          } else {
            return null
          }
        }}
        onScroll={handleOnScroll}
      ></FlatList>
      <BottomArea>
        <LeaveButton onClick={() => setVisible(false)} />
        <SendButton onClick={send} badgeCount={selectedPhotos.length} />
      </BottomArea>
    </Container>
  )
}

export default PhotosSelection
